import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PaymentMethod, isDirectPayment } from 'src/app/helpers/payment-preferences';
import {
  AppearanceAnimation,
  ButtonLayoutDisplay,
  ButtonMaker,
  DialogInitializer,
  DialogLayoutDisplay,
  DisappearanceAnimation,
} from '@costlydeveloper/ngx-awesome-popup';
import { ReservasService } from 'src/app/services/reservas.service';
import { DialogPaymentConfirmComponent } from '../dialog-payment-confirm/dialog-payment-confirm.component';

export interface DialogReservationPayData {
  reservationId: number;
  total: number;
}

/**
 * DialogReservationPayComponent - Diálogo de pago para finalizar una reserva.
 * Reutiliza el mismo flujo de pago que el carrito (efectivo/TPV).
 */
@Component({
  selector: 'app-dialog-reservation-pay',
  templateUrl: './dialog-reservation-pay.component.html',
  styleUrls: ['./dialog-reservation-pay.component.scss'],
})
export class DialogReservationPayComponent {
  paymentMethod: PaymentMethod | null = null;
  cashAmount: number | null = null;
  loading: boolean = false;
  errorMessage: string = '';
  // Cobro directo en curso: se queda en la selección de método, sin pantalla intermedia
  directPaying = false;
  readonly direct: Record<PaymentMethod, boolean> = {
    cash: isDirectPayment('cash'),
    tpv: isDirectPayment('tpv'),
  };
  readonly directWarning = this.buildDirectWarning();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogReservationPayData,
    private dialogRef: MatDialogRef<DialogReservationPayComponent>,
    private reservasService: ReservasService
  ) {}

  get change(): number {
    if (this.cashAmount == null || this.cashAmount < this.data.total) return 0;
    return Math.round((this.cashAmount - this.data.total) * 100) / 100;
  }

  get cashValid(): boolean {
    return this.cashAmount != null && this.cashAmount >= this.data.total;
  }

  selectMethod(method: PaymentMethod): void {
    if (this.loading) return;
    this.paymentMethod = method;
    this.cashAmount = null;
    this.errorMessage = '';

    if (this.direct[method]) {
      this.directPaying = true;
      // Sin importe recibido: se da por entregado el total exacto, cambio 0
      if (method === 'cash') this.cashAmount = this.data.total;
      this.confirm();
    }
  }

  private buildDirectWarning(): string | null {
    if (this.direct.cash && this.direct.tpv) {
      return 'Al pulsar un método de pago se completará el pago, sin más confirmaciones.';
    }
    if (this.direct.cash) {
      return 'Al pulsar «Pagar con efectivo» se completará el pago, sin más confirmaciones.';
    }
    if (this.direct.tpv) {
      return 'Al pulsar «Pagar con tarjeta» se completará el pago, sin más confirmaciones.';
    }
    return null;
  }

  private resetDirectPayment(): void {
    if (!this.directPaying) return;
    this.directPaying = false;
    this.paymentMethod = null;
    this.cashAmount = null;
  }

  confirm(): void {
    if (!this.paymentMethod) return;
    if (this.paymentMethod === 'cash' && !this.cashValid) return;

    this.loading = true;
    this.errorMessage = '';

    const payload: any = {
      work_time_cash_register_id: localStorage.getItem('id_work_time'),
      payment_method: this.paymentMethod,
    };
    if (this.paymentMethod === 'cash') {
      payload.cash = this.cashAmount;
      payload.change = this.change;
    }

    this.reservasService.purchase(this.data.reservationId, payload).subscribe({
      next: (res) => {
        this.loading = false;
        const orderId = res?.order?.id;
        const invoicingId = res?.order?.id_invoicing;
        this.dialogRef.close({ success: true, orderId, invoicingId });
        this.openPrintDialog(this.paymentMethod!, orderId);
      },
      error: (err) => {
        this.loading = false;
        this.resetDirectPayment();
        this.errorMessage =
          err?.error?.message ||
          'No se ha podido procesar el pago. Inténtalo de nuevo.';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close({ success: false });
  }

  private openPrintDialog(channel: string, orderId: number): void {
    const dialogPopup = new DialogInitializer(DialogPaymentConfirmComponent);
    dialogPopup.setCustomData({ channel, id: orderId });
    dialogPopup.setConfig({
      width: '500px',
      layoutType: DialogLayoutDisplay.NONE,
      animationIn: AppearanceAnimation.NONE,
      animationOut: DisappearanceAnimation.NONE,
      escapeKeyClose: true,
    });
    dialogPopup.setButtons([
      new ButtonMaker('', 'info', ButtonLayoutDisplay.INFO),
      new ButtonMaker('Imprimir', 'submit', ButtonLayoutDisplay.SUCCESS),
      new ButtonMaker('Cancelar', 'cancel', ButtonLayoutDisplay.SECONDARY),
    ]);
    dialogPopup.openDialog$().subscribe(() => {});
  }
}
